import React from "react";
import { graphql, PageProps, Link } from "gatsby";
import { Shield, FileCheck, CheckCircle, Clock, Car } from "lucide-react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import HeadingHighlight from "@/components/HeadingHighlight";
import ButtonCTA from "@/components/ButtonCTA";
import Breadcrumb from "@/components/Breadcrumb";

interface UberData {
  site: {
    siteMetadata: {
      siteUrl: string;
    };
  };
}

export const query = graphql`
  query UberPage {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

interface BenefitCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  role?: string;
}

const BenefitCard: React.FC<BenefitCardProps> = ({
  icon: Icon,
  title,
  description,
  role,
}) => (
  <div className="bg-white rounded-lg p-6 shadow-lg" role={role}>
    <div className="flex items-center mb-4">
      <Icon className="w-6 h-6 text-primary mr-3" />
      <h4 className="text-lg sm:text-xl font-bold">{title}</h4>
    </div>
    <p className="text-sm sm:text-base text-gray-600">{description}</p>
  </div>
);

const UberPage: React.FC<PageProps<UberData>> = ({ data }) => {
  const benefits = [
    {
      icon: Shield,
      title: "CRJMC that's Uber Ready",
      description:
        "Get your CRJMC check that meets all requirements for Uber and Uber Eats driver background checks - from an approved third party provider.",
    },
    {
      icon: FileCheck,
      title: "CRJMC - Apply Online",
      description:
        "Apply for your CRJMC check online in minutes. Fast processing helps you submit your Uber driver application faster.",
    },
    {
      icon: CheckCircle,
      title: "Approved Third Party Provider",
      description:
        "CRJMC online checks are fully compliant with Uber's background check requirements for drivers and delivery partners.",
    },
    {
      icon: Clock,
      title: "24/7 Online Access",
      description:
        "CRJMC check for Uber drivers is available anytime - perfect for both new applications and annual background check renewals.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-10 px-4 bg-gradient-to-br from-primary to-primary-dark text-white overflow-hidden">
          <div className="absolute inset-0 z-0 bg-black/40">
            <picture>
              <source
                srcSet="/images/uber-driver/uber-driver-background-check.webp"
                type="image/webp"
              />
              <source
                srcSet="/images/uber-driver/uber-driver-background-check.jpg"
                type="image/jpeg"
              />
              <img
                src="/images/uber-driver/uber-driver-background-check.png"
                alt="Uber driver with cleared background check"
                className="w-full h-full object-cover opacity-30"
              />
            </picture>
          </div>
          <div className="container mx-auto max-w-6xl relative z-10">
            <Breadcrumb
              items={[
                { label: "Ontario", href: "/ontario" },
                { label: "Uber Drivers" },
              ]}
              variant="dark"
              textColor="text-gray-400"
              linkColor="hover:text-emerald-400"
            />
            <div className="text-center mt-10">
              <HeadingHighlight
                as="h1"
                size="4xl"
                className="mb-6 drop-shadow-sm text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight"
              >
                CRJMC Ontario Background Checks for Uber and Uber Eats Drivers
              </HeadingHighlight>
              <p className="text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto drop-shadow-sm">
                Apply for your CRJMC check online today. Fast, reliable criminal
                record checks for your Uber and Uber Eats driver application and
                annual verification.
              </p>
              <div className="inline-block">
                <ButtonCTA />
              </div>
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-white to-gray-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <HeadingHighlight
                as="h2"
                size="4xl"
                className="mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
              >
                So You Want to Drive for Uber in Ontario?
              </HeadingHighlight>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-md">
                <HeadingHighlight
                  as="h3"
                  size="2xl"
                  className="mb-4 text-primary-dark text-xl sm:text-2xl md:text-3xl"
                >
                  Background Check Requirements
                </HeadingHighlight>
                <div className="prose prose-sm sm:prose lg:prose-lg max-w-none text-gray-600">
                  <p className="mb-4">
                    Uber requires all drivers to pass a background check before
                    they can start driving. The CRJMC (Criminal Record and
                    Judicial Matters Check) satisfies the criminal record check
                    requirement for Uber drivers in Ontario.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-md">
                <HeadingHighlight
                  as="h3"
                  size="2xl"
                  className="mb-4 text-primary-dark text-xl sm:text-2xl md:text-3xl"
                >
                  The Critical Importance of Your CRJMC
                </HeadingHighlight>
                <div className="prose prose-sm sm:prose lg:prose-lg max-w-none text-gray-600">
                  <p className="mb-4">
                    All Uber and Uber Eats drivers need to pass this background
                    check to ensure the safety of passengers and customers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center">
              <HeadingHighlight
                as="h2"
                size="4xl"
                className="mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
              >
                Why Choose CRJMC Online for Your Uber Driver Background Check
              </HeadingHighlight>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12"
              role="list"
            >
              {benefits.map((benefit, index) => (
                <BenefitCard key={index} {...benefit} role="listitem" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="p-4 md:p-8 lg:p-12 flex items-center justify-center">
          <div className="max-w-xl text-center">
            <ButtonCTA showSubheader={false} />
          </div>
        </div>

        {/* Requirements Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <HeadingHighlight
                as="h2"
                size="4xl"
                className="mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
              >
                Requirements for Uber Drivers in Ontario
              </HeadingHighlight>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-md">
                <HeadingHighlight
                  as="h3"
                  size="2xl"
                  className="mb-4 text-primary-dark text-xl sm:text-2xl md:text-3xl"
                >
                  What Uber Drivers Need
                </HeadingHighlight>
                <ul className="space-y-3 text-sm sm:text-base">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>
                      Valid driver's license with at least 1 year of driving
                      experience
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Clear background check (CRJMC)</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-8 shadow-md">
                <HeadingHighlight
                  as="h3"
                  size="2xl"
                  className="mb-4 text-primary-dark text-xl sm:text-2xl md:text-3xl"
                >
                  What Uber Eats Delivery Partners Need
                </HeadingHighlight>
                <ul className="space-y-3 text-sm sm:text-base">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Government-issued ID</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Clear background check (CRJMC)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-white rounded-lg p-8 shadow-lg text-center">
              <HeadingHighlight
                as="h2"
                size="4xl"
                className="mb-4 text-2xl sm:text-3xl md:text-4xl"
              >
                Get Your CRJMC for Uber Today
              </HeadingHighlight>
              <p className="text-lg mb-6">
                Fast and secure online application for Uber driver background
                checks.
              </p>
              <ButtonCTA showSubheader={false} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default UberPage;

export const Head = () => (
  <SEO
    title="CRJMC Ontario Background Checks for Uber Drivers | Fast Online Process"
    description="Get your CRJMC check for Uber and Uber Eats driver requirements - fast online processing. Approved third party provider with quick turnaround."
    ogType="website"
    ogTitle="CRJMC Ontario Background Checks for Uber Drivers - Fast Online Process"
    ogDescription="Apply for your CRJMC check online. Fast background checks from an approved third party provider for Uber and Uber Eats drivers in Ontario."
    ogImage="/images/uber-driver/uber-driver-background-check-256.jpg"
  />
);
